import { ContentData } from 'model/ContentData';
import PatientArticleData from 'model/PatientArticleData';
import Config from '../config/constants';
import ConfigurationService from './ConfigurationService';
import VideoDetails from 'model/VideoDetails';
import i18n from 'i18n/I18nInstance';
import { ContentType } from '../model/enums';
import { parseJsonWithReferences } from './utils/JsonUtils';

const getPatientEducation = async () => {
    let content: ContentData[] = [];
    const contentTypeDocument = 'text/html';
    const url = Config.PATIENT_EDUCATION_BASE_URL;

    await fetch(url, getRequest())
        .then((response) => response.text())
        .then((text) => {
            const patEdu = parseJsonWithReferences(text);
            var cardContentType;

            for (let index = 0; index < patEdu.length; index++) {
                if (patEdu[index].contentType === contentTypeDocument) {
                    cardContentType = ContentType.Document;
                } else {
                    cardContentType = ContentType.Video;
                }
                content.push({
                    title: patEdu[index].title,
                    contentId: patEdu[index].contentId,
                    contentTypeId: patEdu[index].contentTypeId,
                    date: new Date(patEdu[index].prescribedDateTime),
                    type: cardContentType,
                    mediaKey: patEdu[index].mediaKey,
                    lastViewedDate: patEdu[index].lastViewedDate
                        ? new Date(patEdu[index].lastViewedDate)
                        : null,
                    recalled: patEdu[index].recalled,
                } as ContentData);
            }
        })
        .catch((error) => {
            console.log(error);
        });

    return content;
};

const getPatientArticle = async (mediaKey: string) => {
    let patientArticleData: PatientArticleData = {};
    const encodedMediaKey = encodeURIComponent(mediaKey);
    const url = `${Config.PATIENT_EDUCATION_BASE_URL}/article/${encodedMediaKey}`;
    try {
        await fetch(url, getRequest())
            .then((response) => response.text())
            .then((text) => {
                const json = parseJsonWithReferences(text);
                patientArticleData = json as PatientArticleData;
            });
    } catch (error) {
        console.log(error);
        throw new Error(
            i18n.translator.formatMessage({
                id: 'contentService.ErrorGetPatientArticle',
                defaultMessage: 'Error when attemping to load patient article.',
            }),
        );
    }
    return patientArticleData;
};

const getPatientEducationVideo = async (mediaKey: string): Promise<VideoDetails> => {
    let content: VideoDetails | null = null;
    const url = `${Config.PATIENT_EDUCATION_BASE_URL}/video/${encodeURIComponent(mediaKey)}`;

    await fetch(url, getRequest())
        .then((response) => response.text())
        .then((text) => {
            const json = parseJsonWithReferences(text);
            content = json as VideoDetails;
        })
        .catch((error) => {
            console.log(`getPatientEducationVideo: received error from getting data`, error);
            throw new Error(
                i18n.translator.formatMessage({
                    id: 'contentService.ErrorGetPatientEducationVideo',
                    defaultMessage: 'Error fetching video',
                }),
            );
        });

    if (!content) {
        throw new Error(
            i18n.translator.formatMessage({
                id: 'contentService.ErrorGetPatientEducationVideo',
                defaultMessage: 'Error fetching video',
            }),
        );
    }

    return content;
};

const postUpdateVideoPercentViewed = async (mediaKey: string, percentViewed: number) => {
    let content: string | null = null;
    const encodedMediaKey = encodeURIComponent(mediaKey);
    const url = `${Config.PATIENT_EDUCATION_BASE_URL}/updateVideoPercentViewed/${encodedMediaKey}?percentViewed=${percentViewed}`;
    try {
        await fetch(url, getRequest())
            .then((response) => response.text()) // convert to plain text
            .then((text) => {});
    } catch (error) {
        console.log(error);
        throw new Error('Error when attemping to update video percent viewed.');
    }
    return content;
};

const downloadPatientArticlePdf = async (mediaKey: string, title: string) => {
    const encodedMediaKey = encodeURIComponent(mediaKey);
    const url = `${Config.PATIENT_EDUCATION_PDF_BASE_URL}/?mediaKey=${encodedMediaKey}`;
    try {
        await fetch(url, getRequest())
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Error when attemping to download patient education pdf.');
                }
                return response.blob(); // convert to plain text
            })
            .then((responseBlob) => {
                const blob = new Blob([responseBlob], { type: 'application/pdf;charset=utf-8' });
                downloadBlob(blob, title);
            })
            .catch((error) => {
                console.log(error);
            });
    } catch (error) {
        console.log(error);
        throw new Error('Error when attemping to download patient education pdf.');
    }
};

function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.click();
    return a;
}

const getRequest = (): RequestInit => {
    const jwt = ConfigurationService.retrieveSessionJwt();
    const bearer = 'Bearer ' + jwt;
    const request: RequestInit = {
        method: 'GET',
        credentials: 'include',
        headers: {
            Authorization: bearer,
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
    };
    return request;
};

const getBase64Pdf = async (docUrl: string): Promise<string> => {
    let content: string | null = null;
    await fetch(docUrl, getRequest())
        .then((response) => response.text())
        .then((base64) => {
            content = base64;
        })
        .catch((error) => {
            console.log(`getBase64Pdf: received error from getting data`, error);
            throw new Error(
                i18n.translator.formatMessage({
                    id: 'contentService.getBase64Pdf',
                    defaultMessage: 'Error fetching base64 pdf',
                }),
            );
        });

    return content;
};

const services = {
    getPatientEducation,
    getPatientArticle,
    getPatientEducationVideo,
    postUpdateVideoPercentViewed,
    downloadPatientArticlePdf,
    downloadBlob,
    getBase64Pdf,
};

export default services;
